import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../services/api';
import './WriteModal.css';

function WriteModal({ onClose, onSave, editingEntry = null, entriesCount = 0 }) {
  const navigate = useNavigate();
  const [text, setText] = useState(editingEntry?.text || '');
  const [mood, setMood] = useState(editingEntry?.mood || null);
  const [isVisible, setIsVisible] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showMoodSelector, setShowMoodSelector] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [quickMoodMode, setQuickMoodMode] = useState(false);
  const [aiLoading, setAiLoading] = useState({ analyse: false, polish: false });
  const [aiError, setAiError] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const [moodPulse, setMoodPulse] = useState(null);
  const quillRef = useRef(null);
  const isEditing = !!editingEntry;
  const isFirstEntry = !isEditing && entriesCount === 0;

  const maxChars = 1000;
  
  // Get plain text length for character count
  const getPlainTextLength = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent?.length || 0;
  };
  
  const charCount = getPlainTextLength(text);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 10);
    setTimeout(() => {
      if (quillRef.current) {
        quillRef.current.focus();
      }
    }, 100);
    
    // Only prevent body scroll if used as modal
    if (onClose) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      
      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
      };
    }
  }, [onClose]);

  // Strip links from pasted content so copy-paste with links becomes plain text
  useEffect(() => {
    if (!isVisible) return;
    const wrapper = quillRef.current;
    if (!wrapper || typeof wrapper.getEditor !== 'function') return;
    const quill = wrapper.getEditor();
    if (!quill || !quill.clipboard || !quill.clipboard.addMatcher) return;
    quill.clipboard.addMatcher('a', (node, delta) => {
      return { ops: [{ insert: node.textContent || '' }] };
    });
  }, [isVisible]);

  const handleClose = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const plainTextLength = getPlainTextLength(text);
    
    if (plainTextLength > 0) {
      setShowDiscardConfirm(true);
      return;
    }
    
    if (onClose) {
      setIsVisible(false);
      setTimeout(onClose, 300);
    } else {
      navigate('/');
    }
  };

  const handleDiscardConfirm = () => {
    setShowDiscardConfirm(false);
    if (onClose) {
      setIsVisible(false);
      setTimeout(onClose, 300);
    } else {
      navigate('/');
    }
  };

  const handleDiscardCancel = () => {
    setShowDiscardConfirm(false);
  };

  const handleSave = () => {
    const plainText = getPlainTextLength(text);
    const canSave = (quickMoodMode && mood) || (plainText > 0 && text !== '<p><br></p>');
    if (!canSave) return;

    if (quickMoodMode && mood) {
      const quickText = text && text !== '<p><br></p>' ? text.trim() : '';
      onSave({ text: quickText, mood }, editingEntry?.id);
    } else {
      onSave({ text: text.trim(), mood: mood || 'neutral' }, editingEntry?.id);
    }

    setSaved(true);
    setTimeout(() => {
      if (onClose) {
        setIsVisible(false);
        setTimeout(onClose, 300);
      } else {
        navigate('/');
      }
    }, 1500);
  };

  const handleTextChange = (content) => {
    const length = getPlainTextLength(content);
    if (length <= maxChars) {
      setText(content);
    }
  };

  // Helper function to extract plain text from HTML
  const getPlainTextFromHTML = (html) => {
    if (!html || html === '<p><br></p>') return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const handleAnalyse = async () => {
    setAiError(null);
    setAnalysisResult(null);

    const plainText = getPlainTextFromHTML(text);
    if (!plainText || plainText.trim().length === 0) {
      setAiError('Please write something before analysing.');
      return;
    }

    setAiLoading(prev => ({ ...prev, analyse: true }));
    try {
      const result = await api.entries.analyseWithAI(plainText);
      setAnalysisResult(result);
      setSummaryExpanded(true);

      if (result.detectedEmotion && result.confidence >= 0.7) {
        setMood(result.detectedEmotion);
        setShowMoodSelector(true);
        setMoodPulse(result.detectedEmotion);
        setTimeout(() => setMoodPulse(null), 1400);
      }
    } catch (error) {
      console.error('AI analyse error:', error);
      setAiError(error.message || 'Analysis failed. Please try again.');
    } finally {
      setAiLoading(prev => ({ ...prev, analyse: false }));
    }
  };

  const handlePolish = async () => {
    setAiError(null);
    const plainText = getPlainTextFromHTML(text);
    if (!plainText || plainText.trim().length === 0) return;

    setAiLoading(prev => ({ ...prev, polish: true }));
    try {
      const result = await api.entries.analyseWithAI(text, { includeRephrase: true });
      if (result.rephrasedText) {
        setText(result.rephrasedText);
      }
      setAnalysisResult(prev => ({ ...prev, ...result }));
    } catch (error) {
      console.error('AI polish error:', error);
      setAiError(error.message || 'Polish failed. Please try again.');
    } finally {
      setAiLoading(prev => ({ ...prev, polish: false }));
    }
  };

  const moods = [
    { 
      value: 'calm', 
      label: 'Calm', 
      color: '#3B82F6',
      description: 'Peaceful and relaxed',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M8 15s1.5-1 4-1 4 1 4 1"/>
          <line x1="9" y1="9" x2="9.01" y2="9"/>
          <line x1="15" y1="9" x2="15.01" y2="9"/>
        </svg>
      )
    },
    { 
      value: 'happy', 
      label: 'Happy', 
      color: '#10B981',
      description: 'Joyful and content',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
          <line x1="9" y1="9" x2="9.01" y2="9"/>
          <line x1="15" y1="9" x2="15.01" y2="9"/>
        </svg>
      )
    },
    { 
      value: 'neutral', 
      label: 'Neutral', 
      color: '#6B7280',
      description: 'Balanced and steady',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="8" y1="15" x2="16" y2="15"/>
          <line x1="9" y1="9" x2="9.01" y2="9"/>
          <line x1="15" y1="9" x2="15.01" y2="9"/>
        </svg>
      )
    },
    { 
      value: 'anxious', 
      label: 'Anxious', 
      color: '#F59E0B',
      description: 'Worried or uneasy',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M8 15s1 -1 4 -1 4 1 4 1"/>
          <path d="M9 10c0-1 .5-1.5 1-1.5s1 .5 1 1.5"/>
          <path d="M15 10c0-1-.5-1.5-1-1.5s-1 .5-1 1.5"/>
        </svg>
      )
    },
    { 
      value: 'sad', 
      label: 'Sad', 
      color: '#8B5CF6',
      description: 'Down or discouraged',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M8 17s1.5-2 4-2 4 2 4 2"/>
          <line x1="9" y1="9" x2="9.01" y2="9"/>
          <line x1="15" y1="9" x2="15.01" y2="9"/>
        </svg>
      )
    },
    { 
      value: 'overwhelmed', 
      label: 'Overwhelmed', 
      color: '#EF4444',
      description: 'Stressed and swamped',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <circle cx="9" cy="10" r="1.5"/>
          <circle cx="15" cy="10" r="1.5"/>
          <path d="M9 16a3 3 0 0 0 6 0"/>
        </svg>
      )
    }
  ];

  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric'
  });
  const timeStr = now.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });

  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ],
  };

  const formats = [
    'bold', 'italic', 'underline',
    'list', 'bullet'
  ];

  return (
    <div 
      className={`write-screen ${isVisible ? 'write-screen--visible' : ''} ${!onClose ? 'write-screen--page' : ''}`}
    >
      <div className="write-screen__container">
        
        <header className="write-screen__header">
          <div className="write-screen__header-left">
            <button 
              className="write-screen__back"
              onClick={handleClose}
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
            </button>
            <div className="write-screen__brand-info">
              <h1 className="write-screen__brand">{isEditing ? "Edit Entry" : "Remori"}</h1>
            </div>
          </div>
          
          <div className="write-screen__header-right">
            <time className="write-screen__datetime">
              <span className="write-screen__date">{dateStr}</span>
              <span className="write-screen__time">{timeStr}</span>
            </time>
          </div>
        </header>

        <div className="write-screen__content">
          <div className="write-screen__writing-card">
            
            {saved && (
              <div className="write-screen__saved-toast">
                <div className="write-screen__saved-toast-content">
                  <div className="write-screen__saved-toast-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                  </div>
                  <span className="write-screen__saved-toast-text">{isEditing ? "Entry updated" : "Entry saved"}</span>
                </div>
              </div>
            )}

            {!isEditing && (
              <div className="write-screen__mode-toggle">
                <button
                  className={`write-screen__mode-btn ${!quickMoodMode ? 'write-screen__mode-btn--active' : ''}`}
                  onClick={() => {
                    setQuickMoodMode(false);
                    setShowMoodSelector(false);
                  }}
                  type="button"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  Full Entry
                </button>
                <button
                  className={`write-screen__mode-btn ${quickMoodMode ? 'write-screen__mode-btn--active' : ''}`}
                  onClick={() => {
                    setQuickMoodMode(true);
                    setShowMoodSelector(true);
                  }}
                  type="button"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                    <line x1="9" y1="9" x2="9.01" y2="9"/>
                    <line x1="15" y1="9" x2="15.01" y2="9"/>
                  </svg>
                  Quick Mood
                </button>
              </div>
            )}

            {quickMoodMode ? (
              <div className="write-screen__quick-mood-container">
                <div className="write-screen__quick-mood-header">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                    <line x1="9" y1="9" x2="9.01" y2="9"/>
                    <line x1="15" y1="9" x2="15.01" y2="9"/>
                  </svg>
                  <span>How do you feel?</span>
                </div>

                <div className="write-screen__quick-mood-grid">
                  {moods.map((m) => (
                    <button
                      key={m.value}
                      type="button"
                      className={`write-screen__quick-mood-card ${mood === m.value ? 'write-screen__quick-mood-card--active' : ''}`}
                      onClick={() => setMood(mood === m.value ? null : m.value)}
                      style={{
                        '--mood-color': m.color
                      }}
                    >
                      <span className="write-screen__quick-mood-icon">
                        {m.icon}
                      </span>
                      <span className="write-screen__quick-mood-label">{m.label}</span>
                      <span className="write-screen__quick-mood-desc">{m.description}</span>
                    </button>
                  ))}
                </div>

                <div className="write-screen__quick-mood-note">
                  <textarea
                    className="write-screen__quick-mood-textarea"
                    value={text === '<p><br></p>' ? '' : text.replace(/<[^>]*>/g, '')}
                    onChange={(e) => setText(e.target.value ? `<p>${e.target.value}</p>` : '<p><br></p>')}
                    placeholder="Add a note (optional)..."
                    rows="2"
                    maxLength={maxChars}
                  />
                  <div className="write-screen__char-count">
                    <span className={charCount > 900 ? 'write-screen__char-count--warning' : ''}>
                      {charCount} / {maxChars}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="write-screen__editor-wrapper">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={text}
                    onChange={handleTextChange}
                    modules={modules}
                    formats={formats}
                    placeholder="Write anything. One word or a thousand."
                    className="write-screen__editor"
                  />
                </div>
                
                <div className="write-screen__char-count">
                  <span className={charCount > 900 ? 'write-screen__char-count--warning' : ''}>
                    {charCount} / {maxChars} characters
                  </span>
                </div>

                <div className="write-screen__ai-tools">
                  {aiError && (
                    <div className="write-screen__ai-error">
                      {aiError}
                    </div>
                  )}
                  <button
                    className="write-screen__ai-btn write-screen__ai-btn--analyse"
                    onClick={handleAnalyse}
                    disabled={charCount === 0 || charCount > 900 || aiLoading.analyse}
                    type="button"
                  >
                    {aiLoading.analyse ? (
                      <>
                        <svg className="spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                        </svg>
                        Analysing…
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
                        </svg>
                        Analyse with AI
                      </>
                    )}
                  </button>
                </div>

                {analysisResult?.summary && (
                  <div className="write-screen__summary-card">
                    <button
                      className="write-screen__summary-toggle"
                      onClick={() => setSummaryExpanded(e => !e)}
                      type="button"
                    >
                      <div className="write-screen__summary-toggle-left">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
                          <path d="M8 12h8M12 8v8"/>
                        </svg>
                        <span>Reflection</span>
                      </div>
                      <svg
                        className={`write-screen__summary-arrow ${summaryExpanded ? 'write-screen__summary-arrow--open' : ''}`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </button>

                    {summaryExpanded && (
                      <div className="write-screen__summary-body">
                        <p className="write-screen__summary-text">{analysisResult.summary}</p>
                        <button
                          className="write-screen__polish-btn"
                          onClick={handlePolish}
                          disabled={aiLoading.polish}
                          type="button"
                        >
                          {aiLoading.polish ? (
                            <>
                              <svg className="spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/>
                              </svg>
                              Polishing…
                            </>
                          ) : (
                            <>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                              Polish my writing
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {!quickMoodMode && (
              <div className="write-screen__mood-section">
                <button 
                  className="write-screen__mood-toggle"
                  onClick={() => setShowMoodSelector(!showMoodSelector)}
                  type="button"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                    <line x1="9" y1="9" x2="9.01" y2="9"/>
                    <line x1="15" y1="9" x2="15.01" y2="9"/>
                  </svg>
                  <span>
                    {mood 
                      ? `Feeling ${moods.find(m => m.value === mood)?.label}` 
                      : 'How do you feel?'}
                  </span>
                  <svg 
                    className={`write-screen__mood-toggle-icon ${showMoodSelector ? 'write-screen__mood-toggle-icon--open' : ''}`}
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                  >
                    <path d="M6 9l6 6 6-6"/>
                  </svg>
                </button>
                
                {showMoodSelector && (
                  <div className="write-screen__moods">
                    {moods.map((m) => (
                      <button
                        key={m.value}
                        type="button"
                        className={`write-screen__mood-chip ${mood === m.value ? 'write-screen__mood-chip--active' : ''} ${moodPulse === m.value ? 'write-screen__mood-chip--pulse' : ''}`}
                        onClick={() => {
                          setMood(mood === m.value ? null : m.value);
                          if (mood !== m.value) {
                            setTimeout(() => setShowMoodSelector(false), 300);
                          }
                        }}
                        style={{
                          '--mood-color': m.color,
                          borderColor: mood === m.value ? m.color : 'transparent'
                        }}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button 
              className="write-screen__save-btn"
              onClick={handleSave}
              disabled={quickMoodMode ? (!mood || saved) : (charCount === 0 || text === '<p><br></p>' || saved)}
            >
              {isEditing ? "Update Entry" : quickMoodMode ? "Save Mood" : "Save Entry"}
            </button>

            {saved && (
              <p className="write-screen__post-save">
                You can write again anytime.
              </p>
            )}
          </div>
        </div>

        <footer className="write-screen__footer">
          <p className="write-screen__footer-text">
            Write freely. No pressure. No judgement.
          </p>
        </footer>

      </div>

      {/* Discard Confirmation Popup */}
      {showDiscardConfirm && (
        <>
          <div 
            className="write-screen__confirm-overlay"
            onClick={handleDiscardCancel}
          />
          <div className="write-screen__confirm-popup">
            <div className="write-screen__confirm-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <h3 className="write-screen__confirm-title">Unsaved Changes</h3>
            <p className="write-screen__confirm-message">
              You have unsaved writing that will be permanently lost if you leave now. Would you like to continue editing or discard this entry?
            </p>
            <div className="write-screen__confirm-actions">
              <button 
                className="write-screen__confirm-btn write-screen__confirm-btn--cancel"
                onClick={handleDiscardCancel}
              >
                Continue Editing
              </button>
              <button 
                className="write-screen__confirm-btn write-screen__confirm-btn--discard"
                onClick={handleDiscardConfirm}
              >
                Discard Entry
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default WriteModal;
